import React, { useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { scheduleAAcceptedFileTypes } from 'helpers/FileHelper';

import { Card, Header, BoldTitle, HorizontalDivider, BoldText, GreyText } from '../common/Card';
import PillButton from 'components/ui/Buttons/PillButton';
import TextButton from 'components/ui/Buttons/TextButton';
import Select from 'components/ui/SelectField';
import SingleFileUpload from 'components/ui/SingleFileUpload/SingleFileUpload';
import TextBox from 'components/ui/TextBox';
import Loading from 'components/ui/Animations/Loading';
import PaperClipIcon from 'assets/paper_clip.png';

import { createScheduleA, fetchScheduleAs } from '../../store/actions';

const CREATE_SCHEDULE_A_STATES = {
  form: 'form',
  loading: 'loading',
  success: 'success',
  error: 'error',
};

const scheduleAErrors = {
  name: false,
  file: false,
};

const FullWidthSelect = styled(Select)`
  margin: 10px 0px;
  width: 100%;
`;

const FullWidthText = styled(TextBox)`
  margin: 10px 0px;
  width: 100%;
`;

const StyledAddScheduleA = styled.div`
  margin: 20px 0px;
  color: ${colors.ULTRAMARINE_BLUE};
`;

const StyledScheduleAUploadContainer = styled.div`
  margin-top: 10px;
`;

const StyledError = styled.div`
  color: ${colors.YUHU_RED};
  margin-bottom: 10px;
`;

const StyledImg = styled.img`
  margin-left: 6px;
  margin-right: 8px;
  height: 23px;
`;

const StyledCtaContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`;

const StyledPillBtnText = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const ScheduleACard = ({
  values,
  touched,
  setFieldValue,
  scheduleAs,
  createScheduleA,
  fetchScheduleAs,
}) => {
  const [scheduleAUpload, setScheduleAUpload] = useState(false);
  const [scheduleA, setScheduleA] = useState(undefined);
  const [scheduleAName, setScheduleAName] = useState('');
  const [errors, setError] = useState(scheduleAErrors);
  const [createScheduleAState, setCreateScheduleAState] = useState(CREATE_SCHEDULE_A_STATES.form);

  const errorPresent = () => {
    let newErrors = { ...errors };
    newErrors = !scheduleAName ? { ...newErrors, name: true } : { ...newErrors, name: false };
    newErrors = !scheduleA ? { ...newErrors, file: true } : { ...newErrors, file: false };

    setError(newErrors);

    return Object.keys(newErrors).some(key => newErrors[key] === true);
  };

  const resetFields = () => {
    setScheduleA(undefined);
    setScheduleAName('');
  };

  const handleScheduleACancel = () => {
    resetFields();
    setScheduleAUpload(false);
    setError('');
    setFieldValue('schedule_a_id', null);
  };

  const handleScheduleASave = async () => {
    if (errorPresent()) {
      return;
    }

    setCreateScheduleAState(CREATE_SCHEDULE_A_STATES.loading);
    const res = await createScheduleA(scheduleAName, scheduleA);
    await fetchScheduleAs();
    if (res) {
      setFieldValue('schedule_a_id', res.id);
      resetFields();
      setScheduleAUpload(false);
    }
    setCreateScheduleAState(CREATE_SCHEDULE_A_STATES.form);
  };

  return (
    <Card>
      <Header>
        <BoldTitle>{getTranslatedString('landlordPortal.rentRoll.renewals.scheduleA')}</BoldTitle>
      </Header>
      <GreyText>{getTranslatedString('landlordPortal.rentroll.renewals.thisIsToAppend')}</GreyText>
      <HorizontalDivider />

      <div>{getTranslatedString('landlordPortal.rentRoll.renewals.selectScheduleA')}</div>

      <FullWidthSelect
        name="scheduleAs"
        showSearch
        placeholder={getTranslatedString('landlordPortal.maintenance.tickets.select')}
        size="large"
        value={values.schedule_a_id}
        onChange={value => setFieldValue('schedule_a_id', value)}
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
      >
        {scheduleAs.map(scheduleA => (
          <Select.Option value={scheduleA.id} key={scheduleA.id}>
            {scheduleA.name}
          </Select.Option>
        ))}
      </FullWidthSelect>

      {scheduleAUpload && (
        <StyledScheduleAUploadContainer>
          <BoldText>
            {getTranslatedString('landlordPortal.rentRoll.renewals.addANewSchedule')}
          </BoldText>
          <StyledScheduleAUploadContainer>
            {getTranslatedString('landlordPortal.rentRoll.renewals.scheduleAName')}
          </StyledScheduleAUploadContainer>
          <FullWidthText
            placeholder={getTranslatedString('landlordPortal.rentRoll.renewals.enterForFutureRef')}
            size="large"
            name="schedule_a_name"
            value={scheduleAName}
            onChange={e => {
              if (e.target.value) {
                setError({ ...errors, name: false });
              } else {
                setError({ ...errors, name: true });
              }
              setScheduleAName(e.target.value);
            }}
          />
          {errors.name && <StyledError>{getTranslatedString('required')}</StyledError>}
          <StyledScheduleAUploadContainer>
            {getTranslatedString('landlordPortal.rentRoll.renewals.attachFile')}
          </StyledScheduleAUploadContainer>
          <SingleFileUpload
            file={scheduleA}
            name="scheduleA"
            onChange={file => {
              if (file) {
                setError({ ...errors, file: false });
              } else {
                setError({ ...errors, file: true });
              }
              setScheduleA(file);
            }}
            onDropVerbiage={getTranslatedString('landlordPortal.rentRoll.renewals.dropFileHere')}
            dropFileVerbiage={getTranslatedString('landlordPortal.rentRoll.renewals.dragAndDrop')}
            browseVerbiage={getTranslatedString('landlordPortal.rentRoll.renewals.orBrowse')}
            rejectFileError={getTranslatedString(
              'landlordPortal.rentRoll.renewals.cannotAcceptFile'
            )}
            acceptedFiles={scheduleAAcceptedFileTypes}
            FileDropIcon={() => <StyledImg alt="paper clip icon" src={PaperClipIcon} />}
          />
          {errors.file && <StyledError>{getTranslatedString('required')}</StyledError>}

          <StyledCtaContainer>
            <TextButton
              type="button"
              onClick={() => {
                handleScheduleACancel();
              }}
              disabled={createScheduleAState === CREATE_SCHEDULE_A_STATES.loading}
            >
              {getTranslatedString('landlordPortal.rentRoll.renewals.cancel')}
            </TextButton>
            <PillButton
              type="button"
              onClick={() => {
                handleScheduleASave();
              }}
              disabled={createScheduleAState === CREATE_SCHEDULE_A_STATES.loading}
            >
              <StyledPillBtnText>
                {createScheduleAState === CREATE_SCHEDULE_A_STATES.loading ? (
                  <React.Fragment>
                    <div>{getTranslatedString('landlordPortal.rentRoll.renewals.saving')}</div>
                    <Loading height={30} width={30} />
                  </React.Fragment>
                ) : (
                  <div>{getTranslatedString('landlordPortal.rentRoll.renewals.save')}</div>
                )}
              </StyledPillBtnText>
            </PillButton>
          </StyledCtaContainer>
        </StyledScheduleAUploadContainer>
      )}
      {!scheduleAUpload && (
        <StyledAddScheduleA onClick={() => setScheduleAUpload(!scheduleAUpload)}>
          {getTranslatedString('landlordPortal.rentRoll.renewals.clickToAddScheduleA')}
        </StyledAddScheduleA>
      )}
    </Card>
  );
};

const mapStateToProps = store => ({
  scheduleAs: store.landlord.renewals.scheduleAs,
});

const mapDispatchToProps = {
  createScheduleA,
  fetchScheduleAs,
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleACard);
